import React, { useState, useEffect } from "react";
import {
  Calendar,
  MapPin,
  User,
  Home,
  QrCode as QrCodeIcon,
  AlertCircle,
  Clock,
  Tag,
  Info,
} from "lucide-react";
import QRCode from "qrcode";
import { llegadaDe } from "../utils/llegada";
import { mostrarDia } from "../utils/fecha";

const styles = {
  card: {
    maxWidth: "500px",
    width: "100%",
    backgroundColor: "var(--bg-elevated)",
    borderRadius: "var(--radius-xl)",
    boxShadow: "var(--shadow-2xl)",
    overflow: "hidden",
  },
  header: {
    background: "var(--gradient-premium)",
    padding: "2rem",
    textAlign: "center",
    color: "white",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    marginBottom: "1rem",
  },
  /* EL COLOR SE DECLARA AQUÍ A PROPÓSITO.
   *
   * El nombre de la residencial va en un `<h1>`, y `src/index.css` tiene una
   * regla `h1, h2, …, h6 { color: var(--text) }`. Esa regla gana sobre el
   * `color: "white"` del encabezado, porque un estilo en línea sólo se hereda
   * a los hijos que no tengan una regla propia — y el `<h1>` la tiene.
   *
   * Con el tema claro `--text` es `--midnight` (#0A1628), que es exactamente
   * la primera parada de `--gradient-premium`: el nombre quedaba del mismo
   * color que su fondo, 1:1 de contraste, invisible. El subtítulo de al lado
   * sí se leía porque `p` no lleva color en la hoja global y heredaba el
   * blanco; por eso el fallo se veía sólo en el nombre.
   *
   * `inherit` toma el blanco del encabezado: 18:1 en claro y 16:1 en oscuro.
   * Importa más que en otras pantallas porque este pase es lo que el residente
   * reenvía por WhatsApp, y el nombre es lo primero que lee un desconocido. */
  title: {
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-2xl)",
    fontWeight: "700",
    letterSpacing: "var(--tracking-tight)",
    color: "inherit",
  },
  subtitle: {
    fontSize: "var(--text-sm)",
    opacity: 0.9,
    marginTop: "0.5rem",
  },
  content: {
    padding: "2rem",
  },
  qrContainer: {
    textAlign: "center",
    marginBottom: "2rem",
  },
  qrImage: {
    maxWidth: "300px",
    width: "100%",
    height: "auto",
    margin: "0 auto",
    borderRadius: "var(--radius-lg)",
    border: "4px solid var(--gold)",
    padding: "1rem",
    backgroundColor: "white",
  },
  info: {
    backgroundColor: "var(--bg-secondary)",
    borderRadius: "var(--radius-lg)",
    padding: "1.5rem",
    marginBottom: "1.5rem",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1rem",
    color: "var(--text)",
  },
  infoIcon: {
    color: "var(--gold)",
    flexShrink: 0,
  },
  infoLabel: {
    fontSize: "var(--text-sm)",
    color: "var(--text-secondary)",
    marginBottom: "0.25rem",
  },
  infoValue: {
    fontSize: "var(--text-base)",
    fontWeight: "600",
    color: "var(--text)",
  },
  /* EL ESTADO DEL PASE, arriba y con color.
   *
   * Es lo primero que el visitante necesita saber y lo que antes no se decía:
   * el pase enseñaba el QR y la fecha, y un pase revocado se veía idéntico a
   * uno válido. La persona se enteraba en la caseta. */
  estado: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "0.75rem 1rem",
    borderRadius: "var(--radius-lg)",
    fontSize: "var(--text-sm)",
    fontWeight: 700,
    marginBottom: "1rem",
  },
  /* La nota del residente va aparte de las instrucciones generales: una dice
   * «muestre el QR» y la otra dice «deje el pedido en recepción». Juntas, la
   * segunda se pierde. */
  notaResidente: {
    backgroundColor: "var(--bg-tertiary)",
    borderRadius: "var(--radius-lg)",
    padding: "1rem",
    fontSize: "var(--text-sm)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    marginBottom: "1rem",
    display: "flex",
    gap: "0.75rem",
    alignItems: "flex-start",
  },
  instructions: {
    backgroundColor: "var(--gold-subtle)",
    borderRadius: "var(--radius-lg)",
    padding: "1rem",
    fontSize: "var(--text-sm)",
    color: "var(--text)",
    textAlign: "center",
    border: "1px solid var(--gold)",
  },
  loading: {
    textAlign: "center",
    padding: "3rem",
  },
  spinner: {
    width: "3rem",
    height: "3rem",
    border: "3px solid var(--border)",
    borderTopColor: "var(--gold)",
    borderRadius: "50%",
    margin: "0 auto",
    animation: "spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite",
  },
};

/**
 * EL ESTADO DEL PASE, en una frase y un color.
 *
 * Se calcula aquí y no se pide al servidor porque el servidor ya mandó todo lo
 * que hace falta —el estado y la ventana de vigencia— y porque el pase tiene
 * que seguir diciendo la verdad si la persona lo abre sin señal, con la
 * pantalla guardada de antes.
 */
const estadoDelPase = (visit) => {
  /* Con los tokens del tema, no con colores fijos. En modo oscuro, aquel rojo
     sobre una tinta translúcida daba 2,8:1 — por debajo del mínimo legible, y
     justo en la línea que dice si el pase sirve o no.
     `sirve` decide si tiene sentido enseñar el código: un pase revocado que
     además dibuja un QR y dice «muéstreselo al guardia» se contradice solo. */
  const rojo = { color: "var(--danger-text)", fondo: "var(--danger-bg)", borde: "var(--danger)", sirve: false };
  const verde = { color: "var(--success-text)", fondo: "var(--success-bg)", borde: "var(--success)", sirve: true };
  const ambar = { color: "var(--warning-text)", fondo: "var(--warning-bg)", borde: "var(--warning)", sirve: false };

  if (visit.status === "Revoked") return { ...rojo, texto: "Pase revocado — no sirve para entrar" };
  if (visit.status === "Denied") return { ...rojo, texto: "Acceso denegado" };
  if (visit.status === "Expired") return { ...rojo, texto: "Pase vencido" };
  if (visit.status === "AwaitingApproval") return { ...ambar, texto: "Esperando la autorización del residente" };
  if (visit.status === "Entered") return { ...verde, texto: "Ya ingresó" };
  if (visit.status === "Exited") {
    return visit.singleUse
      ? { ...rojo, texto: "Este pase era de una sola entrada y ya se usó" }
      : { ...verde, texto: "Salida registrada" };
  }

  if (visit.validUntil && new Date(visit.validUntil) < new Date()) {
    return { ...rojo, texto: "Pase vencido" };
  }
  if (visit.validFrom && new Date(visit.validFrom) > new Date()) {
    return { ...ambar, texto: "El pase todavía no empieza a valer", sirve: true };
  }
  return { ...verde, texto: "Pase válido" };
};

const TIPOS = {
  invitado: "Invitado", familiar: "Familiar", amigo: "Amigo",
  empleado_domestico: "Empleado doméstico", ninera: "Niñera", jardinero: "Jardinero",
  tecnico: "Técnico", proveedor: "Proveedor", delivery: "Delivery",
  transporte: "Uber / transporte", contratista: "Contratista", mensajero: "Mensajero",
  otro: "Otro",
};

const etiquetaTipo = (t) => TIPOS[t] || t;

/** Hasta cuándo vale, dicho como lo diría una persona. */
const vigenciaDe = (visit) => {
  const fmt = (f) =>
    new Date(f).toLocaleString("es-SV", {
      timeZone: "America/El_Salvador",
      dateStyle: "medium",
      timeStyle: "short",
    });

  if (visit.passType === "permanente") return "Permanente";
  if (visit.passType === "recurrente") return "Acceso recurrente";
  if (visit.singleUse) {
    return visit.validUntil ? `Una sola entrada · hasta el ${fmt(visit.validUntil)}` : "Una sola entrada";
  }
  if (visit.validUntil) return `Hasta el ${fmt(visit.validUntil)}`;
  return "El día de la visita";
};

export default function VisitorQRView({ visit, isModal = false }) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateQR();
  }, [visit]);

  const generateQR = async () => {
    if (!visit?.qrCode || !visit?.qrToken) {
      setLoading(false);
      return;
    }

    try {
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}/visitor-qr/${visit.qrCode}?token=${visit.qrToken}`;

      const qrData = await QRCode.toDataURL(fullUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
        errorCorrectionLevel: "M",
      });
      setQrDataUrl(qrData);
    } catch (error) {
      console.error("Error generating QR:", error);
    } finally {
      setLoading(false);
    }
  };

  /* El día de la visita, no una marca de tiempo: se escogió en un
     `<input type="date">` y llega como medianoche UTC, así que leerlo en hora
     de El Salvador mostraba el día anterior en el pase del visitante.
     `vigenciaDe` sí convierte a la zona de El Salvador, y hace bien: ahí
     `validUntil` lleva hora de verdad. */
  const formatDate = (date) =>
    mostrarDia(date, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (loading) {
    return (
      <div style={styles.card}>
        <div style={styles.loading}>
          <div className="animate-spin" style={styles.spinner}></div>
          <p style={{ marginTop: "1rem" }}>Cargando información...</p>
        </div>
      </div>
    );
  }

  if (!visit?.qrCode || !visit?.qrToken) {
    return null;
  }

  const estado = estadoDelPase(visit);

  return (
    <div style={styles.card}>
      {/* La marca de la residencial. El API la manda en `visit.residential`
          —nombre, logo y color— y esta pantalla no la leía: el pase que el
          residente reenvía por WhatsApp llegaba sin decir de qué residencial
          era. Es el único mensaje que ve alguien de fuera. */}
      <div
        style={{
          ...styles.header,
          ...(visit.residential?.primaryColor
            ? { background: visit.residential.primaryColor }
            : {}),
        }}
      >
        <div style={styles.logo}>
          {visit.residential?.logoUrl ? (
            <img
              src={visit.residential.logoUrl}
              alt=""
              style={{ height: 32, width: 32, objectFit: "contain", borderRadius: 6 }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <QrCodeIcon size={32} />
          )}
          <h1 style={styles.title}>
            {visit.residential?.name || "Código de Acceso"}
          </h1>
        </div>
        <p style={styles.subtitle}>
          {visit.residential?.name ? "Pase de visitante" : "Sistema de Control Residencial"}
        </p>
      </div>

      <div style={styles.content}>
        {(() => {
          const e = estado;
          return (
            <div
              style={{
                ...styles.estado,
                backgroundColor: e.fondo,
                color: e.color,
                border: `1px solid ${e.borde}`,
              }}
            >
              {e.texto}
            </div>
          );
        })()}

        {/* El código solo se dibuja si el pase sirve. Enseñar un QR escaneable
            encima de «Pase revocado» hace que el visitante se presente en la
            caseta con un código que no abre nada — que es exactamente la
            conversación que este pase existe para evitar. */}
        {estado.sirve && (
          <div style={styles.qrContainer}>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="Código QR" style={styles.qrImage} />
            )}
          </div>
        )}

        <div style={styles.info}>
          <div style={styles.infoRow}>
            <User size={20} style={styles.infoIcon} />
            <div style={{ flex: 1 }}>
              <div style={styles.infoLabel}>Visitante</div>
              <div style={styles.infoValue}>{visit.visitorName}</div>
            </div>
          </div>

          <div style={styles.infoRow}>
            <Calendar size={20} style={styles.infoIcon} />
            <div style={{ flex: 1 }}>
              <div style={styles.infoLabel}>Fecha de visita</div>
              <div style={styles.infoValue}>{formatDate(visit.date)}</div>
            </div>
          </div>

          <div style={styles.infoRow}>
            <Home size={20} style={styles.infoIcon} />
            <div style={{ flex: 1 }}>
              <div style={styles.infoLabel}>Destino</div>
              <div style={styles.infoValue}>
                Polígono {visit.polygon} - Casa {visit.houseNumber}
              </div>
            </div>
          </div>

          {visit.visitorType && visit.visitorType !== "invitado" && (
            <div style={styles.infoRow}>
              <Tag size={20} style={styles.infoIcon} />
              <div style={{ flex: 1 }}>
                <div style={styles.infoLabel}>Tipo de visita</div>
                <div style={styles.infoValue}>{etiquetaTipo(visit.visitorType)}</div>
              </div>
            </div>
          )}

          {/* HASTA CUÁNDO VALE.
              Sin esto el pase dice «fecha de visita» y el visitante supone que
              sirve ese día y ya. Con pases de 24 horas, de 7 días y
              permanentes, suponer es lo que hace que alguien se presente con
              un pase vencido —o que no venga teniendo uno que todavía vale. */}
          <div style={styles.infoRow}>
            <Clock size={20} style={styles.infoIcon} />
            <div style={{ flex: 1 }}>
              <div style={styles.infoLabel}>Vigencia</div>
              <div style={styles.infoValue}>{vigenciaDe(visit)}</div>
            </div>
          </div>

          {/* Cómo llega. Se le enseña al visitante para que compruebe la placa
              ANTES de llegar: discutir una letra con el guardia, con la fila
              detrás, es justo lo que este sistema debería evitar. */}
          {(() => {
            const { etiqueta, icono: Icono } = llegadaDe(visit);
            return (
              <div style={styles.infoRow}>
                <Icono size={20} style={styles.infoIcon} />
                <div style={{ flex: 1 }}>
                  <div style={styles.infoLabel}>Cómo llega</div>
                  <div style={styles.infoValue}>{etiqueta}</div>
                  {visit.plate && (
                    <div
                      style={{
                        ...styles.infoValue,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                      }}
                    >
                      {visit.plate}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {visit.instructions && (
          <div style={styles.notaResidente}>
            <Info size={18} style={{ color: "var(--gold)", flexShrink: 0, marginTop: "2px" }} />
            <div>
              <strong>De parte de quien lo invitó:</strong>
              <div style={{ marginTop: "0.25rem", whiteSpace: "pre-wrap" }}>{visit.instructions}</div>
            </div>
          </div>
        )}

        {/* Las instrucciones acompañan al código: sin código no hay nada que
            mostrarle al guardia, y decirlo igual contradice al estado. */}
        {estado.sirve ? (
          <div style={styles.instructions}>
            <strong>Instrucciones:</strong> Muestra este código QR al guardia de
            seguridad al llegar a la residencial. Guarda este enlace para tu
            salida.
          </div>
        ) : (
          <div style={styles.instructions}>
            <strong>Este pase no sirve para entrar.</strong> Si cree que es un
            error, comuníquese con la persona que lo invitó: la caseta no puede
            autorizar el acceso sin un pase válido.
          </div>
        )}
      </div>
    </div>
  );
}
